import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Modal from "../../../components/modal";
import searchIcon from "../../../images/ic_search.svg";
import { searchShadowTeamPlayers } from "../../../store/shadowTeam/shadowTeamService";
import { shadowTeamActions } from "../../../store/shadowTeam/shadowTeamSlice";
import { MAX_SHADOW_TEAM_PLAYERS_PER_SLOT } from "../utils/formation";
import { filterShadowTeamSearchResultsForSlot } from "../utils/playerSearch";

/* ── Layout ──────────────────────────────────────────────────── */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  text-align: left;
`;

/* ── Header ──────────────────────────────────────────────────── */

const SlotSubtitle = styled.p`
  color: ${({ theme }) => theme.blueish2};
  font-family: "SatoshiMedium";
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  text-align: left;
  margin: 0 0 4px;
`;

const SlotTitle = styled.h2`
  color: ${({ theme }) => theme.white};
  font-family: "SpaceGrotesk";
  font-size: 22px;
  font-weight: 600;
  text-align: left;
  margin: 0;
`;

/* ── Search ──────────────────────────────────────────────────── */

const SearchWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 8px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.03);
  transition: border-color 0.15s ease;

  &:focus-within {
    border-color: ${({ theme }) => theme.blueish2};
  }

  img {
    width: 14px;
    height: 14px;
    opacity: 0.5;
    flex-shrink: 0;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  border: none;
  background: transparent;
  outline: none;
  color: ${({ theme }) => theme.white};
  font-family: "SatoshiRegular";
  font-size: 14px;

  &::placeholder {
    color: ${({ theme }) => theme.blueish2};
    opacity: 0.6;
  }
`;

const SearchHint = styled.p`
  color: ${({ theme }) => theme.blueish2};
  font-family: "SatoshiRegular";
  font-size: 11px;
  text-align: left;
  margin: 5px 0 0 2px;
  opacity: 0.7;
`;

/* ── Section label ───────────────────────────────────────────── */

const SectionLabel = styled.p`
  color: ${({ theme }) => theme.blueish2};
  font-family: "SatoshiMedium";
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  text-align: left;
  margin: 0 0 8px;
`;

/* ── Search results list ─────────────────────────────────────── */

const ResultsList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.02);
`;

const ResultItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};

  &:last-child {
    border-bottom: none;
  }
`;

const ResultPlayerName = styled.div`
  color: ${({ theme }) => theme.white};
  font-family: "SatoshiRegular";
  font-size: 13px;
  text-align: left;
  flex: 1;
  min-width: 0;

  span {
    display: block;
    color: ${({ theme }) => theme.blueish2};
    font-family: "SatoshiRegular";
    font-size: 11px;
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ResultAction = styled.button`
  border: 1px solid
    ${({ $variant, theme }) =>
      $variant === "full" ? theme.borderColor : $variant === "assigned" ? "transparent" : theme.green};
  background: ${({ $variant, theme }) => {
    if ($variant === "full") return "rgba(255,255,255,0.04)";
    if ($variant === "assigned") return "rgba(173, 196, 39, 0.18)";
    return theme.green;
  }};
  color: ${({ $variant, theme }) =>
    $variant === "full" ? theme.blueish2 : $variant === "assigned" ? theme.green : theme.black};
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  font-family: "SatoshiBold";
  font-size: 11px;
  line-height: 1;
  white-space: nowrap;
  flex-shrink: 0;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const EmptyText = styled.p`
  color: ${({ theme }) => theme.blueish2};
  font-family: "SatoshiRegular";
  font-size: 13px;
  text-align: left;
  padding: 16px 12px;
  margin: 0;
  opacity: 0.7;
`;

/* ── Assigned players list ───────────────────────────────────── */

const AssignedList = styled.div`
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.02);
`;

const AssignedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};
  background: ${({ $dragOver, theme }) => ($dragOver ? `${theme.green}10` : "transparent")};
  opacity: ${({ $dragging }) => ($dragging ? 0.45 : 1)};
  cursor: ${({ $dragDisabled }) => ($dragDisabled ? "default" : "grab")};
  transition: background 0.15s ease, opacity 0.15s ease;
  user-select: none;

  &:last-child {
    border-bottom: none;
  }

  &:active {
    cursor: ${({ $dragDisabled }) => ($dragDisabled ? "default" : "grabbing")};
  }
`;

const DragDotsIcon = styled.span`
  display: grid;
  grid-template-columns: repeat(2, 3px);
  grid-template-rows: repeat(3, 3px);
  gap: 2.5px;
  flex-shrink: 0;
  opacity: ${({ $disabled }) => ($disabled ? 0.15 : 0.35)};
`;

const DragDots = styled.span`
  display: grid;
  grid-template-columns: repeat(2, 3px);
  grid-template-rows: repeat(3, 3px);
  gap: 2.5px;
  flex-shrink: 0;
`;

const DragDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: currentColor;
`;

const AssignedName = styled.div`
  color: ${({ theme }) => theme.white};
  font-family: "SatoshiRegular";
  font-size: 13px;
  text-align: left;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PrimaryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: ${({ theme }) => theme.green};
  color: #1a2200;
  font-family: "SatoshiBold";
  font-size: 10px;
  padding: 3px 8px;
  flex-shrink: 0;
`;

const RemoveBtn = styled.button`
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.3);
  cursor: pointer;
  padding: 3px 5px;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1;
  flex-shrink: 0;
  transition: color 0.12s ease, background 0.12s ease;

  &:hover {
    color: #ff6b6b;
    background: rgba(255, 80, 80, 0.12);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const BackupSlotHint = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-top: 1px solid ${({ theme }) => theme.borderColor};
`;

const BackupDot = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 1.5px dashed ${({ theme }) => theme.blueish2};
  flex-shrink: 0;
  opacity: 0.4;
`;

const BackupHintText = styled.span`
  color: ${({ theme }) => theme.blueish2};
  font-family: "SatoshiRegular";
  font-size: 12px;
  font-style: italic;
  opacity: 0.5;
`;

/* ── Footer ──────────────────────────────────────────────────── */

const Footer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;

const CancelBtn = styled.button`
  flex: 0 0 auto;
  min-width: 100px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 8px;
  color: ${({ theme }) => theme.blueish2};
  font-family: "SatoshiMedium";
  font-size: 13px;
  padding: 10px;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.blueish2};
    color: ${({ theme }) => theme.white};
  }
`;

const SaveBtn = styled.button`
  flex: 0 0 auto;
  min-width: 160px;
  background: ${({ theme }) => theme.green};
  border: none;
  border-radius: 8px;
  color: #1a2200;
  font-family: "SatoshiBold";
  font-size: 13px;
  padding: 10px;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.9;
  }
`;

/* ── Helpers ─────────────────────────────────────────────────── */

const getPlayerFullName = (player) =>
  player?.display_name ||
  `${String(player?.name ?? "").trim()} ${String(player?.surname ?? "").trim()}`.trim() ||
  `#${player?.id}`;

const getSlotSubtitle = (slot) => {
  if (!slot) return "";
  const lineLabel = slot.lineIndex < 0 ? "Goalkeeper" : `Line ${slot.lineIndex + 1}`;
  return `${slot.shortLabel || "GK"} \u2014 ${lineLabel}`;
};

const getSlotPositionTitle = (t, slot) => {
  if (!slot) return t("shadowTeam.playerSearchTitle");
  if (slot.lineIndex < 0) return "Goalkeeper";
  return `Position ${(slot.positionIndex ?? 0) + 1}`;
};

/* ── Component ───────────────────────────────────────────────── */

const ShadowTeamPlayerModal = ({
  isOpen,
  slot,
  assignedPlayers = [],
  assignedPlayerSlotKeyById = {},
  pendingPlayerId = null,
  onClose,
  onSelectPlayer,
  onRemovePlayer,
  onReorderPlayers,
  tutorialSearchSectionRef = null,
  tutorialAssignedSectionRef = null,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { loading, results } = useSelector((state) => state.shadowTeam.playerSearch);

  const [query, setQuery] = useState("");
  const [draggedPlayerId, setDraggedPlayerId] = useState(null);
  const [dragTargetPlayerId, setDragTargetPlayerId] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setDraggedPlayerId(null);
      setDragTargetPlayerId(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const assignedIds = assignedPlayers.map((player) => Number(player?.id));

    if (draggedPlayerId !== null && !assignedIds.includes(draggedPlayerId)) {
      setDraggedPlayerId(null);
    }

    if (dragTargetPlayerId !== null && !assignedIds.includes(dragTargetPlayerId)) {
      setDragTargetPlayerId(null);
    }
  }, [assignedPlayers, dragTargetPlayerId, draggedPlayerId]);

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      dispatch(shadowTeamActions.clearShadowTeamPlayerSearch());
      return;
    }

    const trimmed = query.trim();

    if (trimmed.length < 2) {
      dispatch(shadowTeamActions.clearShadowTeamPlayerSearch());
      return;
    }

    const timeoutId = setTimeout(() => {
      dispatch(searchShadowTeamPlayers(trimmed));
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [dispatch, isOpen, query]);

  const filteredResults = useMemo(
    () => filterShadowTeamSearchResultsForSlot(results, slot, query),
    [query, results, slot]
  );

  const clearDragState = () => {
    setDraggedPlayerId(null);
    setDragTargetPlayerId(null);
  };

  const handleDragStart = (playerId, event) => {
    if (assignedPlayers.length < 2 || pendingPlayerId !== null) {
      event.preventDefault();
      return;
    }

    setDraggedPlayerId(playerId);
    setDragTargetPlayerId(playerId);

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", String(playerId));
    }
  };

  const handleDragEnter = (playerId) => {
    if (draggedPlayerId === null || draggedPlayerId === playerId) return;
    setDragTargetPlayerId(playerId);
  };

  const handleDragOver = (playerId, event) => {
    if (draggedPlayerId === null || draggedPlayerId === playerId) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
    if (dragTargetPlayerId !== playerId) setDragTargetPlayerId(playerId);
  };

  const handleDrop = (targetPlayerId, event) => {
    event.preventDefault();
    const droppedPlayerId = Number(
      draggedPlayerId ?? event.dataTransfer?.getData("text/plain") ?? NaN
    );
    if (!Number.isFinite(droppedPlayerId) || droppedPlayerId === targetPlayerId) {
      clearDragState();
      return;
    }
    onReorderPlayers?.(droppedPlayerId, targetPlayerId);
    clearDragState();
  };

  const canAddMore = assignedPlayers.length < MAX_SHADOW_TEAM_PLAYERS_PER_SLOT;
  const showSearchResults = query.trim().length >= 2;

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={onClose}
      closeIc
      padding="withClose"
      width="460px"
      contentWidth="92%"
      unstyledTypography
    >
      <Wrapper>
        {/* Header */}
        <div>
          <SlotSubtitle>{getSlotSubtitle(slot)}</SlotSubtitle>
          <SlotTitle>{getSlotPositionTitle(t, slot)}</SlotTitle>
        </div>

        {/* Search section */}
        <div ref={tutorialSearchSectionRef}>
          <SearchWrapper>
            <img src={searchIcon} alt="search" />
            <SearchInput
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("shadowTeam.playerSearchPlaceholder")}
            />
          </SearchWrapper>
          {!showSearchResults && (
            <SearchHint>{t("shadowTeam.playerSearchHint")}</SearchHint>
          )}

          {showSearchResults && (
            <div style={{ marginTop: 12 }}>
              <SectionLabel>{t("shadowTeam.playerSearchTitle")}</SectionLabel>
              <ResultsList>
                {loading ? <EmptyText>{t("shadowTeam.searching")}</EmptyText> : null}
                {!loading && !filteredResults.length ? (
                  <EmptyText>{t("shadowTeam.noPlayersFound")}</EmptyText>
                ) : null}
                {!loading &&
                  filteredResults.map((player) => {
                    const playerId = Number(player?.id);
                    const assignedSlotKey = assignedPlayerSlotKeyById[playerId] || null;
                    const isAssignedToTargetSlot = assignedSlotKey === slot?.key;
                    const isAssignedElsewhere = !!assignedSlotKey && assignedSlotKey !== slot?.key;
                    const isTargetFull =
                      assignedPlayers.length >= MAX_SHADOW_TEAM_PLAYERS_PER_SLOT &&
                      !isAssignedToTargetSlot;

                    let actionLabel = t("shadowTeam.addPlayer");
                    let actionVariant = "add";
                    let disabled = pendingPlayerId !== null;

                    if (isAssignedToTargetSlot) {
                      actionLabel = t("shadowTeam.assigned");
                      actionVariant = "assigned";
                      disabled = true;
                    } else if (isTargetFull) {
                      actionLabel = t("shadowTeam.slotFull");
                      actionVariant = "full";
                      disabled = true;
                    } else if (isAssignedElsewhere) {
                      actionLabel = t("shadowTeam.movePlayer");
                    }

                    return (
                      <ResultItem key={player.id}>
                        <ResultPlayerName>
                          {getPlayerFullName(player)}
                          <span>{player?.team?.name || ""}</span>
                        </ResultPlayerName>
                        <ResultAction
                          $variant={actionVariant}
                          disabled={disabled}
                          onClick={() => onSelectPlayer?.(player)}
                          type="button"
                        >
                          {actionLabel}
                        </ResultAction>
                      </ResultItem>
                    );
                  })}
              </ResultsList>
            </div>
          )}
        </div>

        {/* Assigned players */}
        <div ref={tutorialAssignedSectionRef}>
          <SectionLabel>{t("shadowTeam.assignedPlayers")}</SectionLabel>
          <AssignedList data-testid="shadow-assigned-list">
            {!assignedPlayers.length ? (
              <EmptyText>{t("shadowTeam.positionEmpty")}</EmptyText>
            ) : (
              <>
                {assignedPlayers.map((player, index) => {
                  const playerId = Number(player?.id);
                  const isPrimary = index === 0;
                  const isDragDisabled =
                    assignedPlayers.length < 2 || pendingPlayerId !== null;

                  return (
                    <AssignedRow
                      key={`${playerId}-assigned`}
                      data-testid={`shadow-assigned-player-${playerId}`}
                      $dragging={draggedPlayerId === playerId}
                      $dragOver={
                        dragTargetPlayerId === playerId && draggedPlayerId !== playerId
                      }
                      $dragDisabled={isDragDisabled}
                      draggable={!isDragDisabled}
                      onDragStart={(e) => handleDragStart(playerId, e)}
                      onDragEnd={clearDragState}
                      onDragEnter={() => handleDragEnter(playerId)}
                      onDragOver={(e) => handleDragOver(playerId, e)}
                      onDrop={(e) => handleDrop(playerId, e)}
                    >
                      <DragDotsIcon aria-hidden="true" $disabled={isDragDisabled}>
                        <DragDot /><DragDot />
                        <DragDot /><DragDot />
                        <DragDot /><DragDot />
                      </DragDotsIcon>

                      <AssignedName>{getPlayerFullName(player)}</AssignedName>

                      {isPrimary && (
                        <PrimaryBadge>{t("shadowTeam.primaryPlayer")}</PrimaryBadge>
                      )}

                      <RemoveBtn
                        data-testid={`shadow-remove-player-${playerId}`}
                        aria-label={t("shadowTeam.removePlayer")}
                        title={t("shadowTeam.removePlayer")}
                        disabled={pendingPlayerId !== null}
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onRemovePlayer?.(player); }}
                      >
                        ✕
                      </RemoveBtn>
                    </AssignedRow>
                  );
                })}

                {canAddMore && (
                  <BackupSlotHint>
                    <BackupDot />
                    <BackupHintText>Add a backup player...</BackupHintText>
                  </BackupSlotHint>
                )}
              </>
            )}
          </AssignedList>
        </div>

        {/* Footer */}
        <Footer>
          <CancelBtn type="button" onClick={onClose}>
            Cancel
          </CancelBtn>
          <SaveBtn type="button" onClick={onClose}>
            Save assignment
          </SaveBtn>
        </Footer>
      </Wrapper>
    </Modal>
  );
};

export default ShadowTeamPlayerModal;
